import { apiWebhook } from "@/api/webhook.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useToasts } from "@skailer-com/uikit";
import i18n from "@/i18n.ts";
import {
  ApiWebhookDeliveryFilter,
  ApiWebhookFilter,
  ApiWebhookSave,
  ApiWebhookUpdate,
} from "@/types/webhook.types.ts";
import { ApiErrorException, ApiErrors } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const webhookKeys = createQueryKeys("webhooks", {
  list: (filter: ApiWebhookFilter = {}) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiWebhook.search(filter);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  detail: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiWebhook.getById(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  deliveries: (filter: ApiWebhookDeliveryFilter) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiWebhook.searchDeliveries(filter);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useWebhooks = (filter: ApiWebhookFilter = {}) =>
  useQuery({ ...webhookKeys.list(filter) });

export const useWebhook = (id: number) => useQuery({ ...webhookKeys.detail(id) });

export const useWebhookDeliveries = (filter: ApiWebhookDeliveryFilter) =>
  useQuery({ ...webhookKeys.deliveries(filter) });

export interface WebhookSaveParams {
  id?: number;
  payload: ApiWebhookSave | ApiWebhookUpdate;
}

export const useWebhookSave = <TFieldValues extends FieldValues = FieldValues>(
  control?: Control<TFieldValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  const { showError } = useToasts();

  return useMutation({
    mutationFn: async ({ id, payload }: WebhookSaveParams) => {
      const result = id
        ? await apiWebhook.update(id, payload)
        : await apiWebhook.create(payload as ApiWebhookSave);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: webhookKeys.list._def }),
    onError: (error: Error) => {
      onError(error, control);
      if (
        !(error instanceof ApiErrorException) ||
        error.apiError?.status !== ApiErrors.validationError
      ) {
        showError({
          title: i18n.t("common:error"),
          description: i18n.t("core:webhook_operation_error"),
        });
      }
    },
  });
};

const useWebhookAction = <TData>(action: (id: number) => Promise<TData>) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  const { showError } = useToasts();

  return useMutation({
    mutationFn: action,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: webhookKeys.list._def }),
    onError: (error: Error) => {
      onError(error);
      showError({
        title: i18n.t("common:error"),
        description: i18n.t("core:webhook_operation_error"),
      });
    },
  });
};

export const useWebhookDelete = () =>
  useWebhookAction(async (id) => {
    const result = await apiWebhook.delete(id);
    if (result.error || !result.success) throw new ApiErrorException(result.error);
    return id;
  });

export const useWebhookRotateSecret = () =>
  useWebhookAction(async (id) => {
    const result = await apiWebhook.rotateSecret(id);
    if (result.error || !result.data) throw new ApiErrorException(result.error);
    return result.data;
  });

export const useWebhookTestDelivery = () =>
  useWebhookAction(async (id) => {
    const result = await apiWebhook.testDelivery(id);
    if (result.error || !result.data) throw new ApiErrorException(result.error);
    return result.data;
  });

export const useWebhookRedeliver = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  const { showError } = useToasts();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiWebhook.redeliver(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries._def }),
    onError: (error: Error) => {
      onError(error);
      showError({
        title: i18n.t("common:error"),
        description: i18n.t("core:webhook_operation_error"),
      });
    },
  });
};
